import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSignature, FileText, Loader2, RefreshCw } from "lucide-react";
import { adminContracts, adminContractPdf, adminContractPages, adminTestPages, adminTestPdf, setAdvertisingTerms, CONTRACT_STATUS, feeLabel, errMsg } from "@/lib/applyApi";
import { PdfViewer } from "@/components/PdfViewer";
import { AdvertisingTermsPicker, validFee, useFeeOptions } from "@/components/AdvertisingTermsPicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const dt = (v) => (v ? new Date(v).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");

export function ContractPill({ status, testId }) {
  const [l, tone] = CONTRACT_STATUS[status] || [status, ""];
  return <span data-testid={testId} className={`inline-flex border rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{l}</span>;
}

function TermsDialog({ open, data, onClose, onSave }) {
  const [t, setT] = useState({ fee: "10" });
  const [busy, setBusy] = useState(false);
  const opts = useFeeOptions();
  useEffect(() => {
    if (open) setT({ fee: String(data?.terms?.advertising_fee_percent ?? opts.standard) });
  }, [open, data, opts.standard]);
  const signed = data?.status === "signed";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="terms-dialog" className="w-[calc(100%-1.5rem)] rounded-xl">
        <DialogHeader>
          <DialogTitle>{data?.status === "not_started" ? "Set Advertising Terms" : "Change Advertising Terms"}</DialogTitle>
          <DialogDescription>
            {signed ? "The current agreement is signed and locked. Saving creates an AMENDED agreement the seller must sign; the signed one stays in force until then."
              : data?.status === "not_started" ? "Creates the seller's Advertising Agreement with these terms."
                : "The unsigned agreement will be voided and a new version generated for the seller to sign."}
          </DialogDescription>
        </DialogHeader>
        <AdvertisingTermsPicker fee={t.fee} onChange={setT} />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="h-11 rounded-full">Cancel</Button>
          <Button data-testid="terms-save" disabled={busy || !validFee(t.fee)}
            onClick={async () => { setBusy(true); try { await onSave(Number(t.fee)); } finally { setBusy(false); } }}
            className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : signed ? "Create Amendment" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AgreementAdmin({ app, refreshKey, onChanged }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const load = useCallback(() => adminContracts(app.id).then(setData).catch(() => setData(null)), [app.id]);
  useEffect(() => { load(); }, [load, refreshKey, app.status]);
  if (!data) return null;
  const t = data.terms;
  const curWeeks = data.contracts.find((k) => k.status === "awaiting_seller" || k.status === "signed")?.weeks;
  const save = async (fee) => {
    try {
      setData(await setAdvertisingTerms(app.id, fee));
      toast.success("Advertising terms saved — seller message posted");
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, "Could not save terms"));
    }
  };
  return (
    <section data-testid="agreement-admin" className="bg-white border border-stone-200 rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="font-semibold text-stone-900 flex items-center gap-2"><FileSignature className="h-4 w-4 text-amber-800" /> Advertising Agreement</h2>
        <span className="flex items-center gap-1.5">{app.is_test && <span data-testid="agreement-test-badge" className="rounded bg-fuchsia-600 text-white text-[10px] font-extrabold px-1.5 py-0.5">TEST</span>}<ContractPill status={data.status} testId="agreement-status" /></span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><div className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold">Advertising Fee</div><div data-testid="agreement-fee" className="font-bold text-stone-900">{t ? feeLabel(t.advertising_fee_percent) : "Not set"}</div></div>
        <div><div className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold">Weeks (seller)</div><div data-testid="agreement-weeks" className="font-bold text-stone-900">{curWeeks ? `${curWeeks} · $${Number(curWeeks) * 150}` : "—"}</div></div>
      </div>
      {t && <p className="mt-1 text-[11px] text-stone-500">Set by {t.set_by} · {dt(t.set_at)}</p>}
      {app.status === "approved" ? (
        <Button data-testid="agreement-change-terms" variant="outline" onClick={() => setOpen(true)} className="mt-3 w-full h-10 rounded-full">
          {data.status === "not_started" ? "Set terms & create agreement" : data.status === "signed" ? "Change terms (requires amendment)" : "Change terms"}
        </Button>
      ) : <p className="mt-2 text-xs text-stone-500">Approve the application to create its agreement.</p>}
      {data.contracts.length > 0 && (
        <ol data-testid="agreement-versions" className="mt-3 space-y-1.5">
          {data.contracts.map((k) => (
            <li key={k.id} data-testid={`agreement-version-${k.version}`} className="text-xs border border-stone-200 rounded-lg p-2 flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-stone-800">v{k.version} {k.kind === "amendment" && "(amendment)"} · {feeLabel(k.terms.advertising_fee_percent)} · {k.weeks || "—"} wk</div>
                <div data-testid={`agreement-name-${k.version}`} className="text-stone-600 break-words">{k.document_name}</div>
                <ContractPill status={k.status} />
                <div className="text-stone-400 mt-0.5">{k.signed ? `Signed by ${k.signed.signer_name} · ${dt(k.signed.signed_at)} · IP ${k.signed.ip}` : `Created ${dt(k.created_at)} by ${k.created_by}`}</div>
              </div>
              {k.status !== "void" && (
                <div className="shrink-0 flex flex-col gap-1">
                  <button type="button" data-testid={`agreement-pdf-${k.version}`} onClick={() => setViewing({ id: k.id })}
                    className="h-8 px-2 rounded border border-stone-300 inline-flex items-center gap-1 hover:bg-stone-50"><FileText className="h-3.5 w-3.5" />{k.signed ? "View Signed" : "PDF"}</button>
                  {k.signed && (
                    <button type="button" data-testid={`agreement-test-pdf-${k.version}`} onClick={() => setViewing({ id: k.id, test: true })} title="Re-render with the latest layout. Does not change the signed PDF."
                      className="h-8 px-2 rounded border border-dashed border-red-300 text-red-700 inline-flex items-center gap-1 hover:bg-red-50"><RefreshCw className="h-3.5 w-3.5" />Regenerate Test PDF</button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      <TermsDialog open={open} data={data} onClose={() => setOpen(false)} onSave={save} />
      <PdfViewer open={!!viewing} onClose={() => setViewing(null)}
        loadPages={() => (viewing.test ? adminTestPages : adminContractPages)(app.id, viewing.id)}
        loadPdf={() => (viewing.test ? adminTestPdf : adminContractPdf)(app.id, viewing.id)} />
    </section>
  );
}
